import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { getIDFromUrl } from '@rossum/api-client';
import {
  eventsWithoutPrefixes,
  ExtensionEventAction,
} from '@rossum/api-client/hooks';
import { Queue } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import { FilterSelect } from '@rossum/components/filtering/components';
import { createSettings } from '@rossum/components/filtering/helpers';

export const extensionQueryParser = z.object({
  pageSize: z.coerce.number().optional().default(15),
  page: z.coerce.number().optional().default(1),
  search: z.coerce.string().optional(),
  queues: z.coerce.number().optional(),
  events: z.enum(eventsWithoutPrefixes).optional().catch(undefined),
  view: z.enum(['list', 'graph']).optional().default('list'),
});

export type ExtensionsQuery = Partial<z.infer<typeof extensionQueryParser>>;

type UseExtensionsFilterSettingsProps = {
  queues: Queue[] | undefined;
  workspaces: Workspace[] | undefined;
  isQueuesLoading: boolean;
  events: ExtensionEventAction[];
  setFilters: (state: ExtensionsQuery) => void;
  showEventsFilter?: boolean;
};

export const useExtensionsFilterSettings = ({
  queues,
  workspaces,
  isQueuesLoading,
  events,
  setFilters,
  showEventsFilter,
}: UseExtensionsFilterSettingsProps) => {
  const intl = useIntl();

  const workspaceOptions = useMemo(() => {
    return workspaces
      ? workspaces.map(w => ({
          id: w.id.toString(),
          label: w.name,
          options: w.queues.map(q => getIDFromUrl(q).toString()),
        }))
      : undefined;
  }, [workspaces]);

  const settings = useMemo(
    () => [
      createSettings({
        filterKey: 'queues',
        name: intl.formatMessage({
          id: 'features.queues.queueSelect.label',
        }),
        options: workspaceOptions ?? [],
        optionsAreLoading: isQueuesLoading,
        getOptionLabel: option =>
          queues?.find(q => q.id === Number(option))?.name ?? '',
        getChipLabel: value =>
          queues?.find(q => q.id === Number(value))?.name ?? '',
        valueGetter: (currentValue: ExtensionsQuery) => currentValue.queues,
        validateFilter: value =>
          !isQueuesLoading && queues
            ? !queues.find(q => q.id === value)
            : false,
        setFilter: value => setFilters({ queues: value, page: 1 }),
        Component: FilterSelect,
      }),
      createSettings({
        filterKey: 'events',
        name: intl.formatMessage({
          id: 'containers.settings.queues.queue.extensions.selectEvent.label',
        }),
        options: events,
        optionsAreLoading: false,
        getOptionLabel: option => {
          const event = events?.find(e => e === option);

          return event
            ? intl.formatMessage({
                id: `containers.settings.queues.queue.extensions.${event}`,
              })
            : option;
        },
        getChipLabel: event =>
          intl.formatMessage({
            id: `containers.settings.queues.queue.extensions.${event}`,
          }),
        valueGetter: (currentValue: ExtensionsQuery) => currentValue.events,
        setFilter: value => setFilters({ events: value, page: 1 }),
        Component: FilterSelect,
      }),
    ],
    [intl, workspaceOptions, isQueuesLoading, events, queues, setFilters]
  );

  const filteredSettings = useMemo(
    () =>
      settings.filter(s =>
        s.filterKey === 'events' ? showEventsFilter : true
      ),
    [settings, showEventsFilter]
  );

  return filteredSettings;
};
